import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { API_INBOXES_PATH } from 'src/services/api/inboxes/constants';
import {type IInbox} from 'src/services/api/inboxes/types';

type IUpdateInboxRequest = Omit<IInbox, 'id' | 'landlord_user_id' | 'leasing_team_id' | 'is_primary' | 'is_private'>;
type IUpdateInboxResponse = {
    inbox: IInbox
}

export const useUpdateInbox = (id?: number) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<IUpdateInboxResponse, IStandardErrorResponse, IUpdateInboxRequest>(
        async (payload) => {
            return apiClient(`${API_INBOXES_PATH}/${id}`, {
                method: 'patch',
                json: payload,
            }).json<IUpdateInboxResponse>();
        }, {
            onSuccess: (res) => {
                addNotification('Inbox updated', 'success');

                queryClient.setQueryData<IInbox[]>(queryKeys.inboxes, (oldData) => {
                    if (!oldData) {
                        return [res.inbox];
                    }

                    return oldData.map(view => view.id === res.inbox.id ? res.inbox : view);
                });
            }, onError: (error) => {
                const schemaValidationError = error?.messages?.map(message => message.msg).join(', ');
                console.error('Failed to update inbox', error);
                const errorMessage = schemaValidationError || error.msg || 'Failed to update inbox';
                addNotification(errorMessage, 'error');
            }
        }
    );
};
